/**
 * Godot-like InputMap + Input singleton for node scripts.
 * Actions map to KeyboardEvent.code values (and optional mouse buttons).
 */

export type InputAction = {
  keys: string[];
  mouseButtons?: number[];
};

const DEFAULT_ACTIONS: Record<string, InputAction> = {
  ui_accept: { keys: ['Enter', 'NumpadEnter', 'Space'] },
  ui_cancel: { keys: ['Escape'] },
  ui_left: { keys: ['ArrowLeft', 'KeyA'] },
  ui_right: { keys: ['ArrowRight', 'KeyD'] },
  ui_up: { keys: ['ArrowUp', 'KeyW'] },
  ui_down: { keys: ['ArrowDown', 'KeyS'] },
  // Fencing
  move_left: { keys: ['KeyA', 'ArrowLeft'] },
  move_right: { keys: ['KeyD', 'ArrowRight'] },
  move_fast: { keys: ['ShiftLeft', 'ShiftRight'] },
  lunge: { keys: ['Space', 'KeyW'], mouseButtons: [0] },
  fleche: { keys: ['KeyF'] },
  parry_high: { keys: ['KeyQ'], mouseButtons: [2] },
  parry_low: { keys: ['KeyE'] },
  riposte: { keys: ['KeyR'] },
  cam_side: { keys: ['Digit1'] },
  cam_third: { keys: ['Digit2'] },
  cam_fps: { keys: ['Digit3'] },
  pause: { keys: ['Escape', 'KeyP'] },
};

export class InputMap {
  private actions = new Map<string, InputAction>();

  constructor() {
    for (const [k, v] of Object.entries(DEFAULT_ACTIONS)) {
      this.actions.set(k, { keys: [...v.keys], mouseButtons: v.mouseButtons ? [...v.mouseButtons] : undefined });
    }
  }

  has_action(name: string): boolean {
    return this.actions.has(name);
  }

  get_actions(): string[] {
    return Array.from(this.actions.keys()).sort();
  }

  add_action(name: string, action?: InputAction): void {
    this.actions.set(name, action || { keys: [] });
  }

  action_add_event(name: string, code: string): void {
    const a = this.actions.get(name) || { keys: [] };
    if (!a.keys.includes(code)) a.keys.push(code);
    this.actions.set(name, a);
  }

  action_erase_events(name: string): void {
    const a = this.actions.get(name);
    if (a) a.keys = [];
  }

  get_action(name: string): InputAction | null {
    return this.actions.get(name) ?? null;
  }

  /** Serialize for project settings */
  toJSON(): Record<string, InputAction> {
    const o: Record<string, InputAction> = {};
    for (const [k, v] of this.actions) o[k] = { keys: [...v.keys], mouseButtons: v.mouseButtons?.slice() };
    return o;
  }

  fromJSON(data: Record<string, InputAction> | undefined): void {
    if (!data) return;
    for (const [k, v] of Object.entries(data)) {
      this.actions.set(k, {
        keys: [...(v.keys || [])],
        mouseButtons: v.mouseButtons ? [...v.mouseButtons] : undefined,
      });
    }
  }
}

export class EngineInput {
  readonly map = new InputMap();
  private keys = new Set<string>();
  private just = new Set<string>();
  private justUp = new Set<string>();
  private mouseDown = new Set<number>();
  private mouseJust = new Set<number>();
  private mouseJustUp = new Set<number>();
  private enabled = true;
  private bound = false;

  enable(): void {
    this.enabled = true;
    this.bind();
  }

  disable(): void {
    this.enabled = false;
    this.keys.clear();
    this.just.clear();
    this.justUp.clear();
  }

  private bind(): void {
    if (this.bound) return;
    this.bound = true;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.clear);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
  }

  dispose(): void {
    if (!this.bound) return;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.clear);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.bound = false;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.enabled) return;
    // Don't steal typing in inputs
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable))
      return;
    if (!this.keys.has(e.code)) this.just.add(e.code);
    this.keys.add(e.code);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (!this.enabled) return;
    this.keys.delete(e.code);
    this.justUp.add(e.code);
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (!this.enabled) return;
    if (!this.mouseDown.has(e.button)) this.mouseJust.add(e.button);
    this.mouseDown.add(e.button);
  };

  private onMouseUp = (e: MouseEvent): void => {
    if (!this.enabled) return;
    this.mouseDown.delete(e.button);
    this.mouseJustUp.add(e.button);
  };

  private clear = (): void => {
    this.keys.clear();
    this.just.clear();
    this.justUp.clear();
    this.mouseDown.clear();
    this.mouseJust.clear();
    this.mouseJustUp.clear();
  };

  /** Call once at end of frame (Godot Input flushes edge triggers). */
  end_frame(): void {
    this.just.clear();
    this.justUp.clear();
    this.mouseJust.clear();
    this.mouseJustUp.clear();
  }

  is_key_pressed(code: string): boolean {
    return this.keys.has(code);
  }

  is_action_pressed(action: string): boolean {
    const a = this.map.get_action(action);
    if (!a) return false;
    if (a.keys.some((k) => this.keys.has(k))) return true;
    if (a.mouseButtons?.some((b) => this.mouseDown.has(b))) return true;
    return false;
  }

  is_action_just_pressed(action: string): boolean {
    const a = this.map.get_action(action);
    if (!a) return false;
    if (a.keys.some((k) => this.just.has(k))) return true;
    if (a.mouseButtons?.some((b) => this.mouseJust.has(b))) return true;
    return false;
  }

  is_action_just_released(action: string): boolean {
    const a = this.map.get_action(action);
    if (!a) return false;
    if (a.keys.some((k) => this.justUp.has(k))) return true;
    if (a.mouseButtons?.some((b) => this.mouseJustUp.has(b))) return true;
    return false;
  }

  get_axis(negativeAction: string, positiveAction: string): number {
    let v = 0;
    if (this.is_action_pressed(positiveAction)) v += 1;
    if (this.is_action_pressed(negativeAction)) v -= 1;
    return v;
  }

  get_vector(negX: string, posX: string, negY: string, posY: string): { x: number; y: number } {
    return {
      x: this.get_axis(negX, posX),
      y: this.get_axis(negY, posY),
    };
  }
}

/** Global Input used by scripts (assigned by SceneTree). */
export let Input: EngineInput = new EngineInput();

export function setGlobalInput(input: EngineInput): void {
  Input = input;
}
